import { useTranslation } from "react-i18next";
import {
  faCommentAltExclamation,
  faCommentsQuestionCheck,
} from "@fortawesome/pro-regular-svg-icons";
import { faCommentsQuestionCheck as faCommentsQuestionCheckLight } from "@fortawesome/pro-light-svg-icons";
import classNames from "classnames";
import AwesomeIcon from "../AwesomeIcon";

interface SupportEmptyStateProps {
  /** Settings / standalone page uses elevated dark panel */
  isDark?: boolean;
  /** Support modal: Figma 17895:23643 — #D9DBDD panel, centered illustration */
  isModal?: boolean;
}

/**
 * Empty state when no Jira tickets exist.
 * Modal: Figma Playground — "Need to report an issue?" + centered light icon + "No complaints raised yet".
 */
const SupportEmptyState = ({ isDark, isModal }: SupportEmptyStateProps) => {
  const { t } = useTranslation();

  if (isModal) {
    return (
      <div
        className={classNames(
          "flex flex-col flex-1 min-h-0 w-full",
          "rounded-b-[16px] bg-[#d9dbdd] dark:bg-gray-800",
          "overflow-x-clip overflow-y-auto"
        )}
      >
        <div className="flex gap-3 items-start px-[25px] pt-5 pb-4 shrink-0">
          <div className="flex shrink-0 w-[40px] h-[40px] items-center justify-center text-[#474f58] dark:text-gray-100">
            <AwesomeIcon name={faCommentAltExclamation} className="w-[32px] h-[32px]" />
          </div>
          <div className="flex flex-col items-start gap-2 min-w-0">
            <p className="font-sans font-medium text-lg tracking-[0] text-[#474f58] dark:text-gray-100">
              {t("support.report_issue_heading", {
                defaultValue: "Need to report an issue?",
              })}
            </p>
            <p className="font-sans font-normal text-md max-w-lg text-[#474f58] dark:text-gray-400">
              {t("support.report_issue_description", {
                defaultValue:
                  "Raise and track your complaint here. We ensure prompt response.",
              })}
            </p>
          </div>
        </div>

        <div className="flex flex-1 flex-col gap-3 items-center justify-center px-4 py-8 min-h-[200px]">
          <div className="flex shrink-0 size-[108px] items-center justify-center text-[#9ea3a8] dark:text-gray-500">
            <AwesomeIcon name={faCommentsQuestionCheckLight} className="size-[108px]" />
          </div>
          <p className="font-sans font-medium text-base leading-6 text-[#9ea3a8] dark:text-gray-500 text-center whitespace-normal sm:whitespace-nowrap px-2">
            {t("support.no_complaints_yet", {
              defaultValue: "No complaints raised yet",
            })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        isDark
          ? "flex flex-col flex-1 overflow-auto rounded-8 bg-gray-800 min-h-[320px]"
          : "flex flex-col flex-1 overflow-auto rounded-[16px] bg-gray-200 dark:bg-gray-700/50 min-h-[320px]"
      }
    >
      <div className="flex gap-3 items-start pt-5 pb-4 px-[25px] shrink-0">
        <div
          className={
            isDark
              ? "flex shrink-0 w-[40px] h-[40px] items-center justify-center text-gray-300"
              : "flex shrink-0 w-[40px] h-[40px] items-center justify-center text-gray-900 dark:text-gray-200"
          }
        >
          <AwesomeIcon name={faCommentAltExclamation} className="w-[40px] h-[40px]" />
        </div>
        <div className="flex flex-col gap-2 min-w-0">
          <p
            className={
              isDark
                ? "text-gray-100 text-lg font-medium"
                : "text-gray-900 dark:text-gray-100 text-lg font-medium"
            }
          >
            {t("support.report_issue_heading", {
              defaultValue: "Need to report an issue?",
            })}
          </p>
          <p
            className={
              isDark
                ? "text-gray-400 text-md"
                : "text-gray-600 dark:text-gray-400 text-md"
            }
          >
            {t("support.report_issue_description", {
              defaultValue:
                "Raise and track your complaint here. We ensure prompt response.",
            })}
          </p>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 items-center justify-center px-4 py-8">
        <div
          className={
            isDark
              ? "flex shrink-0 size-[108px] items-center justify-center text-gray-500"
              : "flex shrink-0 size-[108px] items-center justify-center text-gray-400 dark:text-gray-500"
          }
        >
          <AwesomeIcon name={faCommentsQuestionCheck} className="size-[108px]" />
        </div>
        <p
          className={
            isDark
              ? "text-base leading-6 font-medium text-gray-500 text-center"
              : "text-base leading-6 font-medium text-gray-400 dark:text-gray-500 text-center"
          }
        >
          {t("support.no_complaints_yet", {
            defaultValue: "No complaints raised yet",
          })}
        </p>
      </div>
    </div>
  );
};

export default SupportEmptyState;
